import {
  BoxProps,
  Center,
  Flex,
  FlexProps,
  Image,
  SimpleGrid,
  SimpleGridProps,
  Text,
  TextProps,
  useRadio,
  useRadioGroup,
} from '@chakra-ui/react';
import { ReactElement } from 'react';

import { Checkmark } from '../assets/Images';

const DefaultSelectedImage = () => (
  <Image
    zIndex={4}
    boxSize="28px"
    src={Checkmark}
    alt="Checkmark"
    position="absolute"
    bottom="-15px"
    left={0}
    right={0}
    mx="auto"
  />
);

type RadioCardButtonProps = BoxProps & {
  compact?: boolean;
  isChecked?: boolean;
  selectedImage?: ReactElement;
};

const RadioCardButton = ({ children, selectedImage, ...props }: RadioCardButtonProps) => {
  const { state, getInputProps, getRadioProps } = useRadio(props);

  // <Flex> puts an error in the console when it takes in `isChecked` as a prop
  // eslint-disable-next-line no-param-reassign
  delete props.isChecked;
  return (
    <Flex as="label">
      <input {...getInputProps()} />
      <Flex
        {...getRadioProps()}
        h="full"
        borderRadius="lg"
        borderRightRadius="lg"
        w="100%"
        py={7}
        border="1px solid"
        borderColor="gray.100"
        alignItems="center"
        justifyContent="center"
        px={{ base: 6, lg: 12 }}
        shadow={state.isChecked ? 'none' : 'md'}
        _hover={{ shadow: state.isChecked ? 'none' : 'lg' }}
        _checked={{ bg: 'royalBlueLight', color: 'white' }}
        bg="white"
        position="relative"
        transitionDuration=".2s"
        cursor="pointer"
        {...props}
      >
        {children}
        {state.isChecked && (selectedImage || <DefaultSelectedImage />)}
      </Flex>
    </Flex>
  );
};

type ButtonedRadioGroupProps = Omit<SimpleGridProps, 'defaultValue' | 'onChange'> & {
  value?: string | number | boolean;
  options: {
    label: ReactElement | string;
    value: string | number | boolean;
    selectedImage?: ReactElement;
  }[];
  name: string;
  defaultValue?: string | number | boolean;
  onChange?: (value: string) => void | Promise<void>;
  reverseOrder?: boolean;
  itemProps?: FlexProps;
  itemTextProps?: TextProps;
  collapseOnMobile?: boolean;
};

const ButtonedRadioGroup = ({
  value,
  options,
  name,
  defaultValue,
  onChange,
  reverseOrder,
  collapseOnMobile = true,
  itemProps,
  itemTextProps,
  ...rest
}: ButtonedRadioGroupProps) => {
  const defaultValueString = defaultValue?.toString();
  const { getRootProps, getRadioProps } = useRadioGroup({
    value: value?.toString(),
    name,
    defaultValue: defaultValueString,
    onChange,
  });

  const reverse = reverseOrder ? '-reverse' : '';

  return (
    <SimpleGrid
      {...getRootProps()}
      direction={{
        base: collapseOnMobile ? `column${reverse}` : `row${reverse}`,
        lg: `row${reverse}`,
      }}
      alignSelf={{ base: 'center', lg: 'start' }}
      spacing={2}
      w="100%"
      overflow="visible"
      {...rest}
    >
      {options.map(({ label, value: optionValue, selectedImage }, i) => (
        <RadioCardButton
          {...itemProps}
          {...getRadioProps({ value: optionValue.toString() })}
          // eslint-disable-next-line react/no-array-index-key
          key={`${i}-${optionValue}`}
          selectedImage={selectedImage}
        >
          <Center>
            <Text textAlign="center" {...itemTextProps}>
              {label}
            </Text>
          </Center>
        </RadioCardButton>
      ))}
    </SimpleGrid>
  );
};

export default ButtonedRadioGroup;
